import React from 'react';

import * as OnboardingActions from 'actions/OnboardingActions';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {NucleotideType} from 'biojs/BioConstants';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import {SequenceTopology, SequenceType} from 'constants/SequenceConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {parseCoordinates} from 'molbio/crispr/BatchCRISPRUtil';
import * as SequenceDAO from 'molbio/dao/SequenceDAO';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {chromosomeToName} from 'util/GenomeUtil';
import {formatThousands} from 'util/NameUtil';

import {
  ChromosomalImportData,
  DBImportSequence,
  SequenceImportOptions,
  RawImportData,
} from './ImportSequenceConstants';
import {importChromosomalRegions, importSequencesFromDB} from './ImportSequenceDAO';

function openImportedSequences(
  sequences: Array<Sequence>,
  importFlow: DNA_SEQUENCE_IMPORT_FLOW
): JQueryPromise<unknown> {
  assert(sequences.length > 0, 'expected at least one sequence');
  const sequenceIds = sequences.map((seq) => seq.id);
  const filePromise = FileStates.get().openFolderItems(sequenceIds);
  notifications.success(
    <>
      Successfully imported <strong>{sequences.length}</strong> {pluralize(sequences.length, 'sequence')}.
    </>
  );
  analytics.track('sequence', 'import_success', {
    product: ANALYTICS_MODULE.MOLBIO,
    importFlow,
    sequenceIds: sequences.map((seq: Sequence) => seq.id),
    liveEnsemblUI: true,
  });
  return filePromise;
}

export async function importFromDB(
  dbImportSequences: Array<DBImportSequence>,
  sequenceImportOptions: SequenceImportOptions,
  onClose: () => void
): Promise<void> {
  analytics.track('sequence', 'import_click', {
    product: ANALYTICS_MODULE.MOLBIO,
    importFlow: DNA_SEQUENCE_IMPORT_FLOW.DB,
    liveEnsemblUI: true,
  });

  FolderDefaults.set(FolderDefaultsKey.INVENTORY, sequenceImportOptions.folderId!);
  try {
    LoadingScreen.show();
    const {results} = await importSequencesFromDB(dbImportSequences, sequenceImportOptions);
    const filePromise = openImportedSequences(results, DNA_SEQUENCE_IMPORT_FLOW.DB);
    filePromise.always(() => {
      LoadingScreen.hide();
    });
    onClose();
    FolderDAOCache.invalidateFolderContents(sequenceImportOptions.folderId);
    OnboardingActions.completeOnboardingTask(
      OnboardingConstants.MODULES.SEQUENCE,
      OnboardingConstants.IMPORT_SEQUENCE
    );
  } catch (error) {
    LoadingScreen.hide();
    analytics.track('sequence', 'import_fail', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.DB,
      errors: [notifications.getFriendlyError(error)],
      liveEnsemblUI: true,
    });
    throw error;
  }
}

function notifyFailedChromosomalImport(errorMessage: string): void {
  notifications.error(errorMessage);
  analytics.track('sequence', 'import_fail', {
    product: ANALYTICS_MODULE.MOLBIO,
    importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
    errors: [errorMessage],
    liveEnsemblUI: true,
  });
}

export async function importSequencesByChromosomalCoords(
  chromosomalData: ChromosomalImportData,
  sequenceImportOptions: SequenceImportOptions,
  onClose: () => void
): Promise<void> {
  analytics.track('sequence', 'import_click', {
    product: ANALYTICS_MODULE.MOLBIO,
    importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
    liveEnsemblUI: true,
  });

  const {genome, chromosomeCoords} = chromosomalData;
  // Ignore any rows that aren't fully filled out without error but ensure that
  // completed rows have valid inputs
  const completeCoords = chromosomeCoords.filter(
    ({chromosome, start, end}) => chromosome != null && start != null && end != null
  );

  if (completeCoords.length === 0) {
    notifyFailedChromosomalImport('No regions specified to import.');
    return;
  }

  const badRegion = completeCoords.find(
    ({chromosome, start, end}) => 1 > start! || start! > end! || end! > chromosome!.length
  );
  if (badRegion != null) {
    const {chromosome} = badRegion;
    notifyFailedChromosomalImport(
      `Start and end for ${chromosomeToName(chromosome!)} must be between 1 and chromosome length \
(${formatThousands(chromosome!.length)}), inclusive. \
Start cannot be higher than end.`
    );
    return;
  }

  const importCoords = [];
  for (const {chromosome, start, end, cdsCoords} of completeCoords) {
    const parsedCDS = parseCoordinates(cdsCoords);
    if (cdsCoords.length > 0 && parsedCDS.length === 0) {
      notifyFailedChromosomalImport(`Invalid format used for CDS coordinates "${cdsCoords}".`);
      return;
    }
    importCoords.push({
      name: chromosome!.name,
      start: start!,
      end: end!,
      cdsCoords: parsedCDS,
    });
  }

  FolderDefaults.set(FolderDefaultsKey.INVENTORY, sequenceImportOptions.folderId!);
  const genomeName = genome!.speciesName != null ? genome!.speciesName : genome!.ensemblName;
  try {
    LoadingScreen.show();
    const {sequences} = await importChromosomalRegions(
      importCoords,
      sequenceImportOptions,
      genomeName,
      genome?.speciesName != null
    );
    const filePromise = openImportedSequences(sequences, DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME);
    filePromise.always(() => {
      LoadingScreen.hide();
    });
    onClose();
  } catch (error) {
    LoadingScreen.hide();
    analytics.track('sequence', 'import_fail', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
      errors: [notifications.getFriendlyError(error)],
      liveEnsemblUI: true,
    });
    throw error;
  }
}

export async function createSequence(
  sequenceImportOptions: SequenceImportOptions,
  rawImportData: RawImportData,
  onClose: () => void
): Promise<void> {
  analytics.track('sequence', 'import_click', {
    product: ANALYTICS_MODULE.MOLBIO,
    importFlow: DNA_SEQUENCE_IMPORT_FLOW.RAW,
  });
  if (sequenceImportOptions.folderId == null) {
    notifications.error('You must choose a folder to put the sequence in.');
    return;
  }
  FolderDefaults.set(FolderDefaultsKey.INVENTORY, sequenceImportOptions.folderId);

  let studyIds: Array<string> = [];
  if (sequenceImportOptions.studySearchItems?.length) {
    studyIds = sequenceImportOptions.studySearchItems.map((studyItem) => studyItem.id);
  }

  try {
    const {id} = await SequenceDAO.create({
      name: rawImportData.name,
      circular: sequenceImportOptions.topology === SequenceTopology.CIRCULAR,
      folder: sequenceImportOptions.folderId,
      nucleotideType: sequenceImportOptions.nucleotideType,
      type:
        sequenceImportOptions.nucleotideType === NucleotideType.DNA
          ? SequenceType.DOUBLE_STRANDED
          : SequenceType.SINGLE_STRANDED,
      bases: rawImportData.bases,
      tagSchemaId: sequenceImportOptions.tagSchemaId,
      studyIds,
    });
    analytics.track('sequence', 'import_success', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.RAW,
      sequenceIds: [id],
    });
    FileStates.get().openFolderItem(id);
  } catch (error) {
    analytics.track('sequence', 'import_fail', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.RAW,
      errors: [notifications.getFriendlyError(error)],
    });
    throw error;
  } finally {
    onClose();
  }
}
